import * as React from 'react';
import { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, TextField, InputAdornment,
  useTheme, useMediaQuery, Pagination, Stack,
  Alert, Select, InputLabel, FormControl, MenuItem,
  LinearProgress, Button
} from '@mui/material';
import { Search as SearchIcon, CloudDownload } from '@mui/icons-material';
import { colors } from '../../utils/colors';
import { getCustomersAPI } from '../../lib/api';
import PageBreadcrumbs from '../../components/common/PageBreadcrumbs';
import CustomerTable from './CustomerTable';

const Customers = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [page, setPage] = useState(1);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [totalPages, setTotalPages] = useState(1);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const getStatusValue = (filter) => {
    if (filter === 'Active') return 1;
    if (filter === 'Inactive') return 0;
    return 2;
  };

  const fetchCustomers = async (currentPage = 1) => {
    try {
      setLoading(true);
      const result = await getCustomersAPI(
        currentPage,
        10,
        searchTerm,
        getStatusValue(statusFilter)
      );
      setCustomers(result?.data?.users || []);
      setTotalPages(result?.data?.total_pages || 1);
    } catch {
      setError('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const delay = setTimeout(() => {
      setPage(1);
      fetchCustomers(1);
    }, 500);
    return () => clearTimeout(delay);
  }, [searchTerm, statusFilter]);

  return (
    <>
      {loading && (
        <Box sx={{ position: 'fixed', top: 0, left: 0, width: '100%', zIndex: 2001 }}>
          <LinearProgress color="primary" />
        </Box>
      )}

      <Box
        sx={{
          width: '100%',
          maxWidth: '100vw',
          overflow: 'hidden',
          px: { xs: 1, sm: 2, md: 3 }
        }}
      >
        <PageBreadcrumbs
          items={[
            { label: 'Dashboard', path: '/dashboard' },
            { label: 'Customers' }
          ]}
        />

        <Paper
          sx={{
            p: { xs: 1.5, sm: 3 },
            borderRadius: 2,
            boxShadow: 'none',
            border: `1px solid ${colors.alpha.black08}`,
            mb: 3,
            overflow: 'hidden',
            maxWidth: '100%',
            width: '100%'
          }}
        >

          <Box
            sx={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              gap: 2,
              mb: 3
            }}
          >
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                textAlign: { xs: 'center', sm: 'left' }
              }}
            >
              Customers Table
            </Typography>

            <Box
              sx={{
                display: 'flex',
                gap: 2,
                ml: isMobile ? 0 : 'auto',
                flexDirection: isMobile ? 'column' : 'row'
              }}
            >
              <TextField
                size="small"
                placeholder="Search customers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  )
                }}
              />

              <FormControl size="small" sx={{ minWidth: 120 }}>
                <Select
                  value={statusFilter}
                  displayEmpty
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="All">All</MenuItem>
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Box>

          </Box>

          {error && <Alert severity="error">{error}</Alert>}

          <CustomerTable
            customers={customers}
            isMobile={isMobile}
          />

          {!loading && customers.length > 0 && (
            <Stack mt={3} alignItems="center">
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, p) => {
                  setPage(p);
                  fetchCustomers(p);
                }}
              />
            </Stack>
          )}
        </Paper>
      </Box>
    </>
  );
};

export default Customers;
